import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, messageErreur } from "@/lib/api";
import { Champ } from "@/components/commun";
import { ChoixListe } from "./ChoixListe";

export const copierLien = (lien) => navigator.clipboard.writeText(lien).then(() => toast.success("Lien copié."), () => toast(lien));

const VIDE = { prenom: "", nom: "", email: "", role: "agent", partenaire_id: "" };

export const UtilisateurDialog = ({ ouvert, setOuvert, utilisateur, referentiels, onEnregistre }) => {
  const edition = Boolean(utilisateur);
  const [f, setF] = useState(VIDE);
  const [lien, setLien] = useState("");
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (ouvert) { setF(utilisateur ? { ...VIDE, ...utilisateur, partenaire_id: utilisateur.partenaire_id || "" } : VIDE); setLien(""); }
  }, [ouvert, utilisateur]);

  const maj = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));

  const soumettre = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    const corps = { prenom: f.prenom, nom: f.nom, role: f.role, partenaire_id: f.role === "partenaire" ? f.partenaire_id || null : null, ...(edition ? { telephone: f.telephone || "" } : {}) };
    try {
      const { data } = edition ? await api.patch(`/utilisateurs/${utilisateur.id}`, corps) : await api.post("/utilisateurs/invitations", { ...corps, email: f.email });
      onEnregistre();
      if (edition) { toast.success("Utilisateur modifié."); setOuvert(false); }
      else { toast.success(`Invitation envoyée à ${data.email}.`); setLien(data.lien_invitation); }
    } catch (err) { toast.error(messageErreur(err)); } finally { setEnvoi(false); }
  };

  return (
    <Dialog open={ouvert} onOpenChange={setOuvert}>
      <DialogContent className="at-popover max-w-[calc(100vw-24px)] sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{edition ? "Modifier l'utilisateur" : "Inviter un utilisateur"}</DialogTitle>
          <DialogDescription className="at-muted">{edition ? utilisateur.email : "Un e-mail d'invitation lui sera envoyé avec un lien pour créer son mot de passe."}</DialogDescription>
        </DialogHeader>
        {lien ? (
          <div className="space-y-4" data-testid="invite-success">
            <p className="text-sm">Lien d'invitation (également visible dans la liste) :</p>
            <p className="at-soft p-3 text-sm break-all" data-testid="invite-link">{lien}</p>
            <div className="flex gap-2">
              <button type="button" className="at-btn at-btn-primary flex-1" onClick={() => copierLien(lien)} data-testid="invite-copy-link"><Copy size={16} />Copier le lien</button>
              <button type="button" className="at-btn at-btn-ghost" onClick={() => setOuvert(false)} data-testid="invite-close">Fermer</button>
            </div>
          </div>
        ) : (
          <form onSubmit={soumettre} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Champ label="Prénom" id="u-prenom" required value={f.prenom} onChange={maj("prenom")} data-testid="user-prenom-input" />
              {edition && <Champ label="Téléphone (assistant WhatsApp)" id="u-tel" type="tel" value={f.telephone || ""} onChange={maj("telephone")} data-testid="user-phone-input" style={{ minHeight: 44 }} />}
              <Champ label="Nom" id="u-nom" required value={f.nom} onChange={maj("nom")} data-testid="user-nom-input" />
            </div>
            {!edition && <Champ label="E-mail" id="u-email" type="email" required value={f.email} onChange={maj("email")} data-testid="user-email-input" />}
            <ChoixListe label="Profil" valeur={f.role} onChange={maj("role")} options={referentiels.roles} testId="user-role-select" />
            {f.role === "partenaire" && (
              <ChoixListe label="Société partenaire" valeur={f.partenaire_id} onChange={maj("partenaire_id")} options={referentiels.partenaires} testId="user-partenaire-select" placeholder="Choisir une société" />
            )}
            <button type="submit" disabled={envoi} className="at-btn at-btn-primary w-full" data-testid="user-submit-button">{edition ? "Enregistrer" : "Envoyer l'invitation"}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
